import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  Typography,
  Chip,
  Stack,
  Divider,
  Tooltip
} from '@mui/material';
import {
  AccountTree,
  LocationOn,
  Business,
  Star,
  ArrowDownward
} from '@mui/icons-material';
import { useRoleDefinitions } from '../../hooks/useRoleDefinitions';
import { usePeopleAdmin } from '../../context/PeopleAdminContext';
import { fetchHierarchyDefinitions } from '../../services/hierarchyService';

/**
 * Visual representation of role hierarchy
 * @param {Object} props - Component props
 * @param {Function} props.onRoleClick - Callback when a role is clicked
 * @returns {React.Component} Role hierarchy visualization
 */
function RoleHierarchy({ onRoleClick }) {
  const { getSortedRoles, getActiveRoles } = useRoleDefinitions();
  const { clientId } = usePeopleAdmin();
  const [hierarchyLabels, setHierarchyLabels] = useState({});

  const activeRoles = getActiveRoles();
  const sortedRoles = getSortedRoles().filter(r => r.isActive !== false);

  useEffect(() => {
    if (clientId) {
      fetchHierarchyDefinitions(clientId)
        .then(levels => {
          const labels = {
            'LOCATION_LEVEL': 'Location Level'
          };
          levels.forEach(level => {
            if (['ORGLEVEL_1', 'ORGLEVEL_2', 'ORGLEVEL_3'].includes(level.levelId)) {
              labels[level.levelId] = level.label + ' Level';
            }
          });
          setHierarchyLabels(labels);
        })
        .catch(error => {
          console.error('Failed to load hierarchy labels:', error);
          // Use defaults
          setHierarchyLabels({
            'LOCATION_LEVEL': 'Location Level',
            'ORGLEVEL_1': 'District Level',
            'ORGLEVEL_2': 'Region Level',
            'ORGLEVEL_3': 'Division Level'
          });
        });
    }
  }, [clientId]);

  const getLevelIcon = (assignmentLevel) => {
    if (assignmentLevel === 'LOCATION_LEVEL') return <LocationOn fontSize="small" />;
    return <Business fontSize="small" />;
  };

  const getLevelColor = (assignmentLevel) => {
    switch (assignmentLevel) {
      case 'LOCATION_LEVEL':
        return 'primary';
      case 'ORGLEVEL_1':
        return 'secondary';
      case 'ORGLEVEL_2':
        return 'info';
      case 'ORGLEVEL_3':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getLevelLabel = (assignmentLevel) => {
    return hierarchyLabels[assignmentLevel] || assignmentLevel || 'Unknown';
  };

  const orgLevelToNumber = (orgLevel) => {
    switch (orgLevel) {
      case 'LOCATION_LEVEL': return 0;
      case 'ORGLEVEL_1': return 1;
      case 'ORGLEVEL_2': return 2;
      case 'ORGLEVEL_3': return 3;
      default: return 999;
    }
  };

  // Group roles by assignmentLevel
  const rolesByLevel = sortedRoles.reduce((acc, role) => {
    const level = role.assignmentLevel || 'UNKNOWN';
    if (!acc[level]) {
      acc[level] = [];
    }
    acc[level].push(role);
    return acc;
  }, {});

  // Sort levels in the correct order
  const levelOrder = ['LOCATION_LEVEL', 'ORGLEVEL_1', 'ORGLEVEL_2', 'ORGLEVEL_3'];
  const levels = Object.keys(rolesByLevel).sort((a, b) => {
    const indexA = levelOrder.indexOf(a);
    const indexB = levelOrder.indexOf(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  if (levels.length === 0) {
    return (
      <Card sx={{ p: 4 }}>
        <Typography variant="body1" color="text.secondary" align="center">
          No active role definitions to display in hierarchy.
        </Typography>
      </Card>
    );
  }

  return (
    <Card>
      <Box p={3}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Role Hierarchy
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Visual representation of organizational role structure
        </Typography>

        <Box mt={3}>
          {levels.map((level, index) => (
            <Box key={level}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}
                >
                  {orgLevelToNumber(level)}
                </Box>
                <Typography variant="subtitle1" fontWeight="medium">
                  {getLevelLabel(level)}
                </Typography>
              </Box>

              <Box ml={7} mb={3}>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  {rolesByLevel[level].map((role) => (
                    <Tooltip
                      key={role.id}
                      title={
                        <Box>
                          <Typography variant="body2">Code: {role.roleCode}</Typography>
                          <Typography variant="body2">Level: {getLevelLabel(role.assignmentLevel)}</Typography>
                          {role.category && (
                            <Typography variant="body2">Category: {role.category}</Typography>
                          )}
                          {role.metadata?.description && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {role.metadata.description}
                            </Typography>
                          )}
                        </Box>
                      }
                    >
                      <Card
                        sx={{
                          p: 2,
                          minWidth: 150,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          border: '2px solid',
                          borderColor: role.isDefault ? 'warning.main' : 'divider',
                          backgroundColor: level === 'LOCATION_LEVEL' ? 'action.hover' : 'background.paper',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 3,
                            borderColor: 'primary.main'
                          }
                        }}
                        onClick={() => onRoleClick && onRoleClick(role)}
                      >
                        <Stack spacing={1}>
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            {getLevelIcon(role.assignmentLevel)}
                            {role.isDefault && (
                              <Tooltip title="Default Role">
                                <Star fontSize="small" color="warning" />
                              </Tooltip>
                            )}
                          </Box>
                          <Typography variant="body1" fontWeight="bold">
                            {role.roleName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {role.roleCode}
                          </Typography>
                          {level === 'LOCATION_LEVEL' && role.category && (
                            <Chip
                              label={role.category}
                              size="small"
                              color="default"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      </Card>
                    </Tooltip>
                  ))}
                </Stack>
              </Box>

              {index < levels.length - 1 && (
                <Box display="flex" justifyContent="center" my={2}>
                  <ArrowDownward color="action" />
                </Box>
              )}
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box display="flex" gap={3} justifyContent="center" flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1}>
            <LocationOn fontSize="small" color="primary" />
            <Typography variant="caption">Location (Store) Level</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Business fontSize="small" color="secondary" />
            <Typography variant="caption">Organizational Unit Levels</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Star fontSize="small" color="warning" />
            <Typography variant="caption">Default Role</Typography>
          </Box>
        </Box>
      </Box>
    </Card>
  );
}

RoleHierarchy.propTypes = {
  onRoleClick: PropTypes.func
};

export default RoleHierarchy;